//React
import * as React from "react";
import { connect } from "react-redux";

//Store
import { MainStore } from "./../../store/MainStore";

//Material UI Components
import AddIcon from "material-ui-icons/Add";
import AnnoucementIcon from "material-ui-icons/Announcement";
import CheckIcon from "material-ui-icons/Check";
import ClearIcon from "material-ui-icons/Clear";
import ExpandMoreIcon from "material-ui-icons/ExpandMore";
import Avatar from "material-ui/Avatar";
import Button from "material-ui/Button";
import Card from "material-ui/Card";
import Checkbox from "material-ui/Checkbox";
import Collapse from "material-ui/transitions/Collapse";
import Dialog, { DialogActions, DialogContent, DialogContentText, DialogTitle } from "material-ui/Dialog";
import Divider from "material-ui/Divider";
import IconButton from "material-ui/IconButton";
import List, { ListItem, ListItemAvatar, ListItemSecondaryAction, ListItemText, ListItemIcon } from "material-ui/List";
import TextField from "material-ui/TextField";
import Typography from "material-ui/Typography";

//Icones
import { Build, LightbulbOutline, Power, Tab } from "material-ui-icons";

//Managers
import { StyleManager } from "./../../managers/StyleManager";

//Framework
import { Category_e, getEnumByLabel } from "../../framework/Category";
import { UserProfile_e } from "../../framework/user/UserProfile";
import { NetworkManager, MethodRequest_e } from "../../managers/NetworkManager";
import ColorManager from "../../managers/ColorManager";

//Style
const styles = StyleManager.getStyles().BorrowsView;

/**
 * @description Render the correct Icon corresponding to the category given in params
 * @author Lucas Fridez <lfridez@artionet.com>
 * @param {*} category
 */
const renderMaterialIcon = (category) => {
    category = getEnumByLabel(category);

    if (category === Category_e.BOARDS) {
        return (<Tab />);
    }
    if (category === Category_e.CABLES) {
        return (<Power />);
    }
    if (category === Category_e.LIGHTS) {
        return (<LightbulbOutline />);
    }
    if (category === Category_e.MATERIALS) {
        return (<Build />);
    }
    return <Build />;
};

/**
 * @description Interface for the Borrow State
 * @author Lucas Fridez <lfridez@artionet.com>
 * @interface BorrowsState
 */
interface BorrowsState {
    borrows: Array<any>;
    borrowsToValidate: Array<any>;
    borrowsToValidateChecked: Array<any>;
    openDialog: boolean;
    showMaterials: Array<any>;
}

/**
 * @description Interface for the Borrow Props
 * @author Lucas Fridez <lfridez@artionet.com>
 * @interface BorrowProps
 */
interface BorrowProps {
    refresh: any;
    userProfile: UserProfile_e;
}

function getElementOffset(el) {
    const rect = el.getBoundingClientRect();

    return {
        left: rect.left + window.pageXOffset,
        top: rect.top + window.pageYOffset
    };
}

function isElementInViewport(el) {
    const rect = el.getBoundingClientRect();
    return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
}

/**
 * @description Contain the Borrows View
 * @author Lucas Fridez <lfridez@artionet.com>
 * @export
 * @class Borrows
 * @extends {React.Component}
 */
export default connect<{}, {}, BorrowProps>((state) => state)(class Borrows extends React.Component<BorrowProps, BorrowsState> {

    clickedProduct = {
        "damaged": false,
        "damages": [],
        "date": "",
        "icon": "",
        "id": 0,
        "name": "",
        "place": "",
        "price": ""
    };

    private commentDialog = "";

    /**
     * Creates an instance of Borrows.
     * @author Lucas Fridez <lfridez@artionet.com>
     * @param {any} props
     * @memberof Borrows
     */
    constructor(props) {
        super(props);
        this.state = {
            borrows: [],
            borrowsToValidate: [],
            borrowsToValidateChecked: [
                []
            ],
            openDialog: false,
            showMaterials: []
        };
    }

    /**
     * @description Show the Damaged Dialog
     * @author Lucas Fridez <lfridez@artionet.com>
     * @memberof Borrows
     */
    handleClickOpenDialog = () => {
        this.setState({
            openDialog: true
        });
    }

    /**
     * @description Click on a damaged material
     *              Show or hide the material damages
     * @author Lucas Fridez <lfridez@artionet.com>
     * @memberof Borrows
     */
    public handleClickDamagedMaterial = (key) => {
        const index = this.state.showMaterials.indexOf(key);
        if (index > -1) {
            this.setState((prevState) => ({
                showMaterials: this.state.showMaterials.filter((e) => e !== key)
            }));
        } else {
            this.setState((prevState) => ({
                showMaterials: [...prevState.showMaterials, key]
            }));
        }
    }

    /**
     * @description Define if the damaged material shows its damages
     * @author Lucas Fridez <lfridez@artionet.com>
     * @returns {boolean} If the damages will be shown or not
     * @memberof Borrows
     */
    isDamagedProductShown = (key): boolean => {
        return this.state.showMaterials.indexOf(key) > -1 ? true : false;
    }

    /**
     * @description Hide the Damaged Dialog
     * @author Lucas Fridez <lfridez@artionet.com>
     * @memberof Borrows
     */
    handleCloseDialog = () => {
        this.setState({ openDialog: false });
    }

    /**
     * @description Validate an array of borrows
     * @author Lucas Fridez <lfridez@artionet.com>
     * @memberof Borrows
     */
    public async validateBorrows(key) {
        const { borrowsToValidate, borrowsToValidateChecked } = this.state;

        const borrowsToSendAPI = [];
        borrowsToValidate[key]["borrows"].forEach((borrow: any) => {
            borrowsToSendAPI.push({
                "id": borrow.id,
                "validated": borrowsToValidateChecked[key].indexOf(borrow.id) > -1 ? true : false
            });
        });

        MainStore.showOverlay("Validation en cours");
        await NetworkManager.request("borrows/validate", MethodRequest_e.PUT, borrowsToSendAPI);
        await this.getBorrowsToValidate();
        MainStore.hideOverlay();
    }

    public async unvalidateBorrows(key) {
        MainStore.showOverlay("Invalidation des emprunts en cours");
        await NetworkManager.request("borrows/unvalidate", MethodRequest_e.PUT, this.state.borrowsToValidateChecked[key]);
        await this.getBorrowsToValidate();
        MainStore.hideOverlay();
    }

    /**
     * @description Define a Damaged Product
     * @author Lucas Fridez <lfridez@artionet.com>
     * @memberof Borrows
     */
    defineDamagedProduct = () => {
        const id = this.state.borrows.findIndex((product) => product.id == this.clickedProduct.id);
        this.state.borrows[id].damaged = true;
        if (this.state.borrows[id].damages === null) {
            this.state.borrows[id].damages = new Array(this.commentDialog);
        } else {
            this.state.borrows[id].damages.push(this.commentDialog);
        }
        NetworkManager.request("damages", MethodRequest_e.POST, {
            id: this.state.borrows[id].pid,
            text: this.commentDialog
        });
        this.commentDialog = "";
    }

    /**
     * @description Handle the checked attribute for a Material to be validated
     * @author Lucas Fridez <lfridez@artionet.com>
     * @memberof Borrows
     */
    handleCheckMaterialToValidate = (key, mainKey) => {
        try {
            const { borrowsToValidateChecked } = this.state;
            const currentIndex = borrowsToValidateChecked[mainKey].indexOf(key);
            const newChecked = [...borrowsToValidateChecked];

            if (currentIndex === -1) {
                newChecked[mainKey].push(key);
            } else {
                newChecked[mainKey].splice(currentIndex, 1);
            }

            this.setState({
                borrowsToValidateChecked: newChecked
            });
        } catch (err) {
            console.log(err);
        }
    }

    checkAllBorrowsForMoment = (key) => {
        const borrowsToCheck = this.state.borrowsToValidate;
        const borrowsAlreadyChecked = this.state.borrowsToValidateChecked;

        const borrowsChecked = [];
        if (this.state.borrowsToValidate[key]["borrows"].length != this.state.borrowsToValidateChecked[key].length) {
            borrowsToCheck[key]["borrows"].forEach((borrow: any) => {
                borrowsChecked.push(borrow.id);
            });
        }
        borrowsAlreadyChecked[key] = borrowsChecked;
        this.setState({
            borrowsToValidateChecked: borrowsAlreadyChecked
        });
    }

    /**
     * @description Check if a Material to be validated is checked
     * @author Lucas Fridez <lfridez@arionet.com>
     * @memberof Borrows
     */
    isProductToValidateChecked = (key, mainKey) => {
        if (this.state.borrowsToValidateChecked[mainKey] && this.state.borrowsToValidateChecked[mainKey].indexOf(key) === -1) {
            return false;
        } else {
            return true;
        }
    }

    /**
     * @description Get all borrows to validate
     * @author Lucas Fridez <lfridez@artionet.com>
     * @memberof Borrows
     */
    public async getBorrowsToValidate() {
        this.setState({
            borrowsToValidate: await NetworkManager.request(`borrows/tovalidate`, MethodRequest_e.GET)
        });
        const nextBorrowsToValidateChecked = [];
        this.state.borrowsToValidate.map(() => {
            nextBorrowsToValidateChecked.push([]);
        });
        this.setState({
            borrowsToValidateChecked: nextBorrowsToValidateChecked
        });
    }

    async getBorrowsForUser() {
        this.setState({
            borrows: await NetworkManager.request(`borrows/users/${NetworkManager.getUserId()}`, MethodRequest_e.GET)
        });
        if(this.props.userProfile == UserProfile_e.AGENCY_MANAGER) {
            this.getBorrowsToValidate();
        }
    }

    /**
     * @description Component will be mounted in the App
     * @author Lucas Fridez <lfridez@artionet.com>
     * @memberof Borrows
     */
    public async componentWillMount() {
        this.setState({
            borrows: await NetworkManager.request(`borrows/users/${NetworkManager.getUserId()}`, MethodRequest_e.GET)
        });
        if(this.props.userProfile == UserProfile_e.AGENCY_MANAGER) {
            this.getBorrowsToValidate();
        }
    }

    public async componentWillReceiveProps(nextProps: BorrowProps) {
        if (this.props.refresh != nextProps.refresh) {
            MainStore.showOverlay("Mise à jour des emprunts...");
            this.setState({
                borrows: await NetworkManager.request(`borrows/users/${NetworkManager.getUserId()}`, MethodRequest_e.GET)
            });
            if(this.props.userProfile == UserProfile_e.AGENCY_MANAGER) {
                this.getBorrowsToValidate();
            }
            MainStore.hideOverlay();
        }
    }

    isAllChecked = (key) => {
        return this.state.borrowsToValidate[key]["borrows"].length == this.state.borrowsToValidateChecked[key].length;
    }

    /**
     * @description Render the Borrows View
     * @author Lucas Fridez <lfridez@artionet.com>
     * @returns Home View
     * @memberof Home
     */
    render() {
        const materialsLength = this.state.borrows.length;

        if (this.props.userProfile != UserProfile_e.FITTER) {
            if (this.state.borrowsToValidate.length != this.state.borrowsToValidateChecked.length) {
                return null;
            }
        }

        return (
            <div id="borrows">
                {this.props.userProfile == UserProfile_e.AGENCY_MANAGER &&
                    <Typography type="headline" gutterBottom>Emprunts à valider</Typography>
                }
                {this.props.userProfile == UserProfile_e.AGENCY_MANAGER && this.state.borrowsToValidate.length == 0 &&
                    <Typography>Aucun emprunt à valider.</Typography>
                }
                {this.props.userProfile == UserProfile_e.AGENCY_MANAGER && this.state.borrowsToValidate.map((borrowForMoment, key) => (
                    <div key={key}>
                        <Typography type="subheading">
                            {borrowForMoment.user} - depuis {borrowForMoment.from} ({borrowForMoment.date})
                    </Typography>
                        <span style={{
                            alignItems: "center",
                            display: "flex",
                            justifyContent: "flex-end",
                            marginRight: "3px"
                        }}>
                            <Typography>Tout cocher</Typography>
                            <Checkbox
                                onClick={(e: any) => this.checkAllBorrowsForMoment(key)}
                                title="Tout cocher"
                                checked={this.isAllChecked(key)}
                            />
                        </span>
                        <Card style={{ marginBottom: 16 }}>
                            <List style={styles.list}>
                                {borrowForMoment["borrows"].map((borrow, borrowKey) => (
                                    <ListItem
                                        key={borrowKey}
                                        button onClick={() => { this.handleCheckMaterialToValidate(borrow.id, key); }}
                                        style={borrow.damaged ? styles.damagedMaterial : styles.undamagedMaterial}>
                                        <ListItemAvatar>
                                            <Avatar style={styles.iconMaterial}>
                                                {renderMaterialIcon(borrow.category)}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemSecondaryAction>
                                            <Checkbox
                                                disableRipple
                                                checked={this.isProductToValidateChecked(borrow.id, key)}
                                                style={styles.checkbox}
                                            />
                                        </ListItemSecondaryAction>

                                        <ListItemText primary={borrow.name} />
                                    </ListItem>
                                ))}
                            </List>
                        </Card>

                        {this.state.borrowsToValidateChecked[key] && this.state.borrowsToValidateChecked[key].length > 0 &&
                            <div style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                marginTop: 16,
                                right: 0
                            }}>
                                {this.state.borrowsToValidateChecked[key] && this.state.borrowsToValidateChecked[key].length == 1 &&
                                    <Button fab mini
                                        onClick={() => { this.unvalidateBorrows(key); }}
                                        style={{
                                            backgroundColor: ColorManager.error,
                                            marginRight: 4
                                        }}>
                                        <ClearIcon style={{ color: ColorManager.text }} />
                                    </Button>
                                }
                                <Button fab mini
                                    onClick={() => { this.validateBorrows(key); }}
                                    style={{
                                        backgroundColor: ColorManager.success,
                                        marginLeft: 4
                                    }}>
                                    <CheckIcon style={{ color: ColorManager.text }} />
                                </Button>
                            </div>
                        }
                    </div>
                ))}

                <Typography type="headline" gutterBottom>Mes outils empruntés</Typography>

                <Card>
                    <List style={styles.list}>
                        {this.state.borrows.map((material, key) => (
                            <div key={key}>
                                <ListItem
                                    button onClick={() => { if (material.damaged) { this.handleClickDamagedMaterial(key); } }}
                                    style={material.damaged ? styles.damagedMaterial : styles.undamagedMaterial}>
                                    <ListItemAvatar>
                                        <Avatar style={styles.iconMaterial}>
                                            {renderMaterialIcon(material.category)}
                                        </Avatar>
                                    </ListItemAvatar>

                                    <ListItemText
                                        primary={material.name}
                                        secondary={
                                            <span style={{
                                                color: "#222",
                                                display: "flex",
                                                flexDirection: "column"
                                            }}>
                                                <span>Loué le {material.date}</span>
                                                <span>Depuis {material.origin}</span>
                                                <span>{material.validated == "0" ? "En attente de validation" : "Emprunt validé"}</span>
                                                {material.remark != "" &&
                                                <span>Remarques : {material.remark}</span>
                                                }
                                            </span>
                                        } />
                                    <ListItemSecondaryAction style={{ top: "calc(50% + 6px)" }}>
                                        <IconButton aria-label="Annoucement" onClick={() => { this.handleClickOpenDialog(); this.clickedProduct = material; }}
                                            style={{ backgroundColor: "#ffffff", boxShadow: "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)", marginRight: 4, height: 36, width: 36 }}>
                                            <AddIcon style={styles.iconComment} />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                                {material.damaged && material.damages &&
                                    <Collapse in={this.isDamagedProductShown(key)} timeout="auto" unmountOnExit>
                                        {material.damages ? material.damages.map((damage, sKey) => (
                                            <List dense component="div" disablePadding key={sKey}>
                                                <ListItem>
                                                    <ListItemIcon>
                                                        <AnnoucementIcon style={styles.iconComment} />
                                                    </ListItemIcon>
                                                    <ListItemText inset primary={damage} />
                                                </ListItem>
                                                <Divider inset />
                                            </List>
                                        )) : ""}
                                    </Collapse>
                                }
                                {materialsLength !== key + 1 && <Divider style={{ backgroundColor: "rgba(42,181,99,0.9)"/*"#2CBF68"*/ }} />}
                            </div>
                        ))}
                    </List>
                </Card>

                {/* DIALOG */}
                <Dialog
                    open={this.state.openDialog}
                    disableBackdropClick={true}
                    aria-labelledby="Matériel endommagé">

                    <DialogTitle id="form-dialog-title">Matériel endommagé</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Vous désirez annoncer le matériel <strong>{this.clickedProduct.name}</strong> comme étant endommagé. Veuillez décrire les dégâts ci-dessous.
                        </DialogContentText>
                        <TextField
                            autoFocus multiline margin="dense" id="damage" label="Description du dommage" type="text" fullWidth
                            onChange={(event) => { this.commentDialog = event.target.value; }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button style={{
                            backgroundColor: "#E53935",
                            color: "#FFF",
                            fontWeight: 300
                        }} onClick={this.handleCloseDialog} raised>Annuler</Button>
                        <Button style={styles.validationButton} onClick={() => { this.defineDamagedProduct(); this.handleCloseDialog(); }} raised>Annoncer</Button>
                    </DialogActions>
                </Dialog>
            </div>
        );
    }
});



// WEBPACK FOOTER //
// ./src/app/UI/views/Borrows.tsx